import {
  AgGridReact,
  AgGridReactProps,
  CustomCellEditorProps,
  CustomCellRendererProps,
} from "ag-grid-react";
import { CellValue } from "@inscopix/ideas-hyperformula";
import {
  CellEditRequestEvent,
  ColumnResizedEvent,
  SelectionChangedEvent,
  ColGroupDef,
  ColDef,
  ColumnMovedEvent,
  GetRowIdParams,
  DragStartedEvent,
  ICellRendererParams,
  GridReadyEvent,
  IHeaderGroupParams,
  IHeaderParams,
  IRowNode,
} from "ag-grid-community";
import { useDataTableContext } from "../store/DataTableProvider";
import { ColumnHeaderBaseMemo } from "../column-headers/ColumnHeaderBase";
import { CellRendererBaseMemo } from "../cell-renderers/CellRendererBase";
import { memo, useCallback, useEffect, useMemo } from "react";
import assert from "assert";
import { isDefined } from "utils/isDefined";
import { isNonNull } from "utils/isNonNull";
import { captureException } from "@sentry/react";
import { CellEditorBaseMemo } from "../cell-editors/CellEditorBase";
import { formatDate } from "utils/formatDate";
import { CellRendererQuickActionsMemo } from "../cell-renderers/CellRendererQuickActions";
import { TaskStatus } from "types/constants";
import { isNonNullish } from "utils/isNonNullish";
import { CellRendererTaskUserMemo } from "../cell-renderers/CellRendererTaskUser";
import { CellRendererTaskIdMemo } from "../cell-renderers/CellRendererTaskId";
import { CellRendererTaskLogsMemo } from "../cell-renderers/CellRendererTaskLogs";
import { ColumnGroupHeaderBaseMemo } from "../column-group-headers/ColumnGroupHeaderBase";
import { isEqual } from "lodash";
import { CellEditorToolVersionMemo } from "../cell-editors/CellEditorToolVersion";
import { CellEditorComputeResourcesMemo } from "../cell-editors/CellEditorComputeResources";
import { RESOURCE_TIERS_BY_ID, ResourceTier } from "types/ResourceTiers";
import { isNullish } from "utils/isNullish";
import { formatInterval } from "utils/formatInterval";
import { roundToSignificant } from "utils/roundToSignificant";
import moment from "moment";
import { useDataTableClipboard } from "./useDataTableClipboard";
import { formatCellValue } from "../store/DataTableProvider.helpers";
import { convertCellValueToStaticFormula } from "../store/SideEffects";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { ToolSpec } from "types/json-schemas/type_guards";
import AppLoading from "components/AppLoading/AppLoading";
import { ToolSpecV2 } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { filesize } from "utils/filesize";

/**
 *
 * Formats tool resource tiers into a readable string
 * @param toolSpec Tool Spec
 * @param resourceTierId Tier choice id; null uses tool spec defaults
 * @returns Friendly readable resources string
 */
export const formatToolResources = (
  toolSpec: Pick<ToolSpecV2, "resources" | "credits">,
  resourceTierId: ResourceTier["id"] | null,
) => {
  const resourceTier =
    resourceTierId !== null
      ? RESOURCE_TIERS_BY_ID[resourceTierId]
      : { id: null, ...toolSpec.resources, credits: toolSpec.credits };

  const cpu =
    resourceTier?.cpu !== undefined
      ? `${resourceTier.cpu} vCPU${resourceTier.cpu === 1 ? "" : "s"}, `
      : "";
  const gpu =
    resourceTier?.gpu !== undefined && resourceTier.gpu !== 0 ? `GPU, ` : "";
  const mem =
    resourceTier?.memory !== undefined
      ? `${filesize(resourceTier.memory * 1_000_000) as string} RAM, `
      : "";
  const credits = `${resourceTier.credits} credits/hr`;
  return cpu + gpu + mem + credits;
};

/**
 * Disables all column definition properties related to editing.
 * @param defs Array of column and column group definitions.
 */
const disableEditing = (defs: (DataTableColDef | DataTableColGroupDef)[]) => {
  defs.forEach((def) => {
    if ("editable" in def) {
      def.editable = false;
      def.suppressPaste = true;
      def.suppressFillHandle = true;
    } else if ("children" in def) {
      disableEditing(def.children);
    }
  });
};

export type DataTableColDef = ColDef<DataTableRowData> & {
  // Gets the values that should be written to clipboard when copying cells. If
  // null is returned, the column's cells will be not be written to the clipboard.
  clipboardValueGetter: (node: IRowNode<DataTableRowData>) => {
    external: string;
    internal: string;
  } | null;
};

export interface DataTableColGroupDef extends ColGroupDef<DataTableRowData> {
  children: DataTableColDef[];
}

export type DataTableRowData = {
  id: string;
  editable: boolean;
  task: {
    id: string;
    status: TaskStatus;
    created: string;
    credits: number | null;
    duration: string;
    user: string;
  } | null;
  toolVersion: {
    id: number;
    version: string;
    resources: ToolSpec["resources"];
    credits: number;
  } | null;
  resourceTierId: ResourceTier["id"] | null;
  cells: {
    formula: string;
    value: CellValue;
    isCustomFormula: boolean;
  }[];
};

const colDefRowIndex = {
  valueGetter: ({ node }) => (node !== null ? node.childIndex + 1 : null),
  cellStyle: { color: "grey" },
  colId: "rowNumber",
  headerName: "",
  sortable: false,
  pinned: "left",
  resizable: false,
  width: 78,
  lockPosition: "left",
  suppressHeaderMenuButton: true,
  checkboxSelection: true,
  headerCheckboxSelection: true,
  suppressFillHandle: true,
  suppressPaste: true,
  clipboardValueGetter: (node) => {
    const rowIndex = (node.childIndex + 1).toString();
    return { external: rowIndex, internal: rowIndex };
  },
} satisfies DataTableColDef;

const colDefToolVersion = {
  colId: "tool_version",
  headerName: "Tool Version",
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  sortable: false,
  resizable: true,
  width: 100,
  valueGetter: ({ data }) => data?.toolVersion?.version ?? null,
  editable: ({ data }) => isNullish(data?.task),
  cellStyle: (params) => {
    const toolVersion = params?.data?.toolVersion?.version ?? null;
    return toolVersion === null
      ? { color: "grey", fontStyle: "italic" }
      : undefined;
  },
  cellEditor: (props: CustomCellEditorProps<DataTableRowData>) => (
    <CellEditorToolVersionMemo
      rowId={props.data.id}
      selectedToolVersion={props.data.toolVersion}
      stopEditing={props.stopEditing}
    />
  ),
  suppressFillHandle: true,
  suppressPaste: true,
  clipboardValueGetter: (node) => {
    const semanticVersion = node.data?.toolVersion?.version ?? "";
    return { external: semanticVersion, internal: semanticVersion };
  },
} satisfies DataTableColDef;

const colDefComputeResources = {
  colId: "compute_resources",
  headerName: "Compute Resources",
  sortable: false,
  resizable: true,
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  cellStyle: (params) => {
    const resourceTier = params.data?.resourceTierId ?? null;
    return resourceTier === null
      ? { color: "grey", fontStyle: "italic" }
      : { color: "initial", fontStyle: "initial" };
  },
  valueGetter: (params) => {
    const toolVersion = params.data?.toolVersion;
    const resourceTier = params.data?.resourceTierId ?? null;
    return toolVersion ? formatToolResources(toolVersion, resourceTier) : null;
  },
  editable: ({ data }) => isNullish(data?.task),
  cellEditor: (props: CustomCellEditorProps<DataTableRowData>) => (
    <CellEditorComputeResourcesMemo
      rowId={props.data.id}
      toolVersionCredits={props.data.toolVersion?.credits ?? 0}
      toolVersionResources={props.data.toolVersion?.resources ?? {}}
      resourceTierId={props.data.resourceTierId}
      stopEditing={props.stopEditing}
    />
  ),
  suppressFillHandle: true,
  suppressPaste: true,
  clipboardValueGetter: (node) => {
    const toolVersion = node.data?.toolVersion;
    const resourceTier = node.data?.resourceTierId ?? null;
    const value = toolVersion
      ? formatToolResources(toolVersion, resourceTier)
      : "";
    return { external: value, internal: value };
  },
} satisfies DataTableColDef;

export const colGroupDefTask = {
  groupId: "task",
  headerName: "Task",
  marryChildren: true,
  children: [
    {
      colId: "task_id",
      headerName: "ID",
      suppressMovable: true,
      lockPosition: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: false,
      width: 80,
      cellRenderer: (params: ICellRendererParams<DataTableRowData>) => {
        const task = params.data?.task;
        return isNonNullish(task) ? (
          <CellRendererTaskIdMemo taskId={task.id} />
        ) : null;
      },
      suppressFillHandle: true,
      suppressPaste: true,
      clipboardValueGetter: (node) => {
        const taskId = node.data?.task?.id ?? "";
        return { external: taskId, internal: taskId };
      },
    },
    {
      colId: "task_log",
      headerName: "Log",
      suppressMovable: true,
      lockPosition: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: true,
      width: 50,
      cellRenderer: (params: ICellRendererParams<DataTableRowData>) => {
        const task = params.data?.task;
        return isNonNullish(task) ? (
          <CellRendererTaskLogsMemo
            taskId={task.id}
            taskStatus={task.status}
            taskCreated={task.created}
          />
        ) : null;
      },
      suppressFillHandle: true,
      suppressPaste: true,
      clipboardValueGetter: () => null,
    },
    {
      colId: "task_user",
      headerName: "User",
      suppressMovable: true,
      lockPosition: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: true,
      width: 55,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellRenderer: (params: ICellRendererParams<DataTableRowData>) => {
        const task = params.data?.task;
        return isNonNullish(task) ? (
          <CellRendererTaskUserMemo userId={task.user} />
        ) : null;
      },
      suppressFillHandle: true,
      suppressPaste: true,
      clipboardValueGetter: (node) => {
        const userId = node.data?.task?.user ?? "";
        return { external: userId, internal: userId };
      },
    },
    {
      colId: "task_date_started",
      headerName: "Date Started",
      suppressMovable: true,
      lockPosition: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: true,
      width: 140,
      valueGetter: (params) => {
        const task = params.data?.task;
        return isNonNullish(task) ? formatDate(task.created) : null;
      },
      suppressFillHandle: true,
      suppressPaste: true,
      clipboardValueGetter: (node) => {
        const date = node.data?.task?.created ?? "";
        return { external: date, internal: date };
      },
    },
    {
      colId: "task_duration",
      headerName: "Duration",
      suppressMovable: true,
      lockPosition: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: true,
      width: 80,
      valueGetter: (params) => {
        const taskDuration = params.data?.task?.duration;

        if (isNonNullish(taskDuration)) {
          const momentDuration = moment.duration(taskDuration, "seconds");
          return formatInterval({
            __typename: "Interval",
            years: momentDuration.years(),
            months: momentDuration.months(),
            days: momentDuration.days(),
            hours: momentDuration.hours(),
            minutes: momentDuration.minutes(),
            seconds:
              momentDuration.seconds() + momentDuration.milliseconds() / 1000,
          });
        }

        return null;
      },
      suppressFillHandle: true,
      suppressPaste: true,
      clipboardValueGetter: (node) => {
        const duration = node.data?.task?.duration ?? "";
        return { external: duration, internal: duration };
      },
    },
    {
      colId: "task_compute_credits",
      headerName: "Compute Credits",
      suppressMovable: true,
      lockPosition: "right",
      suppressHeaderMenuButton: true,
      sortable: false,
      resizable: true,
      width: 130,
      valueGetter: (params) => {
        const credits = params.data?.task?.credits;
        return typeof credits === "number" ? roundToSignificant(credits) : null;
      },
      suppressFillHandle: true,
      suppressPaste: true,
      clipboardValueGetter: (node) => {
        const credits = (node.data?.task?.credits ?? "").toString();
        return { external: credits, internal: credits };
      },
    },
  ] satisfies DataTableColDef[],
} satisfies DataTableColGroupDef;

const colDefQuickActions = {
  colId: "quick_actions",
  cellRenderer: (params: ICellRendererParams<DataTableRowData>) => {
    if (params.data !== undefined) {
      return <CellRendererQuickActionsMemo rowId={params.data.id} />;
    }
  },
  headerName: "",
  pinned: "right",
  sortable: false,
  resizable: true,
  width: 144,
  suppressMovable: true,
  lockPosition: "right",
  suppressHeaderMenuButton: true,
  cellStyle: { padding: 0 },
  suppressFillHandle: true,
  suppressPaste: true,
  clipboardValueGetter: () => null,
} satisfies DataTableColDef;

// When no analysis parameter sources are mapped, this column is rendered as a
// placeholder. Empty column groups will not be rendered unless they contain at
// least one visible column.
const colDefSourcesPlaceholder = {
  colId: `sources_placeholder`,
  headerName: "",
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  sortable: false,
  resizable: false,
  suppressFillHandle: true,
  suppressPaste: true,
  clipboardValueGetter: () => {
    return { external: "", internal: "" };
  },
} satisfies DataTableColDef;

/**
 * Component that renders columns, rows and cells for data tables and analysis
 * tables
 */
const DataTableInner = ({
  suppressHeaderMenus,
  suppressReorderCols,
}: DataTableProps) => {
  const selectedTable = useDataTableContext((s) => {
    const table = s.tables.find((table) => table.id === s.selectedTableId);
    assert(isDefined(table));
    return table;
  });
  const gridApi = useDataTableContext((s) => s.gridApi);
  const setGridApi = useDataTableContext((s) => s.setGridApi);
  const setSelectedRowIds = useDataTableContext((s) => s._setSelectedRowIds);
  const setCellFormula = useDataTableContext((s) => s.setCellFormula);
  const resizeColumn = useDataTableContext((s) => s.resizeColumn);
  const setColumnOrder = useDataTableContext((s) => s.setColumnOrder);
  const { fillOperation, processDataFromClipboard, sendToClipboard } =
    useDataTableClipboard(selectedTable.id);
  const isEditingPermitted = useProjectPermissionStore(
    (s) => s.permissions.edit,
  );

  const customColDefs: (DataTableColDef | DataTableColGroupDef)[] =
    useMemo(() => {
      // Initialize column group definitions
      const groupDefs: DataTableColGroupDef[] = selectedTable.columnGroups.map(
        (columnGroup) => ({
          groupId: columnGroup.id,
          headerName: columnGroup.name,
          headerGroupComponent: (
            params: IHeaderGroupParams<DataTableRowData>,
          ) => (
            <ColumnGroupHeaderBaseMemo
              displayName={params.displayName}
              tableId={selectedTable.id}
              tableKind={selectedTable.kind}
              columnGroupId={columnGroup.id}
              columnGroupDefinition={columnGroup.definition ?? undefined}
              suppressMenu={suppressHeaderMenus}
            />
          ),
          children: [],
        }),
      );

      // Accumulator holding all column and column group definitions
      const allDefinitions: (DataTableColDef | DataTableColGroupDef)[] = [];

      // Create column definitions
      selectedTable.columns.forEach((column, index) => {
        const colDef: DataTableColDef = {
          cellEditor: (props: CustomCellEditorProps<DataTableRowData>) => (
            <CellEditorBaseMemo
              api={props.api}
              onValueChange={props.onValueChange}
              stopEditing={props.stopEditing}
              tableId={selectedTable.id}
              tableKind={selectedTable.kind}
              columnId={column.id}
              columnName={column.name}
              columnDefinition={column.definition}
              isIdColumn={column.is_id_column}
              rowId={props.data.id}
              formula={props.data.cells[index]?.formula ?? ""}
              value={props.data.cells[index]?.value ?? null}
              eventKey={props.eventKey}
            />
          ),
          cellRenderer: (props: CustomCellRendererProps<DataTableRowData>) => {
            const rowData = props.data;
            assert(isDefined(rowData));
            return (
              <CellRendererBaseMemo
                api={props.api}
                eParentOfValue={props.eParentOfValue}
                tableId={selectedTable.id}
                tableKind={selectedTable.kind}
                columnId={column.id}
                columnName={column.name}
                columnDefinition={column.definition}
                isColumnRequired={column.required}
                rowId={rowData.id}
                rowNode={props.node}
                formula={rowData.cells[index]?.formula ?? ""}
                value={rowData.cells[index]?.value ?? null}
                isCustomFormula={rowData.cells[index]?.isCustomFormula ?? false}
                isCellEditable={column.editable && (rowData.editable ?? false)}
                isRowExecuted={rowData.task !== null}
              />
            );
          },
          cellStyle: { padding: 0 },
          colId: column.id,
          headerComponent: (params: IHeaderParams<DataTableRowData>) => {
            const group = selectedTable.columnGroups.find(
              (group) => group.id === column.group,
            );
            return (
              <ColumnHeaderBaseMemo
                displayName={params.displayName}
                eGridHeader={params.eGridHeader}
                index={index}
                tableId={selectedTable.id}
                tableKind={selectedTable.kind}
                columnId={column.id}
                columnDefinition={column.definition}
                columnGroupDefinition={group?.definition ?? undefined}
                isColumnDeletable={column.deletable}
                isPinned={column.pinned}
                helpText={column.help_text}
                isIdColumn={column.is_id_column}
                defaultFormula={column.default_formula}
                suppressMenu={suppressHeaderMenus}
              />
            );
          },
          headerName: column.name,
          editable: ({ data }) => {
            const isColumnEditable = column.editable;
            const isRowEditable = data?.editable ?? false;
            return isColumnEditable && isRowEditable;
          },
          suppressPaste: ({ data }) => {
            const isColumnEditable = column.editable;
            const isRowEditable = data?.editable ?? false;
            return !isColumnEditable || !isRowEditable;
          },
          suppressFillHandle: !column.editable,
          resizable: true,
          pinned: column.pinned,
          lockPinned: true,
          width: column.width,
          // Prevent value-getter from dictating when cells re-render
          equals: () => false,
          valueGetter: (params) => params.data?.cells?.[index]?.value ?? null,
          clipboardValueGetter: (node) => {
            const value = node.data?.cells?.[index]?.value ?? null;
            return {
              external: formatCellValue(value),
              internal: convertCellValueToStaticFormula(value),
            };
          },
        };

        // If the column belongs to a group, add it to the group definition.
        if (column.group !== null) {
          const group = groupDefs.find(
            (group) => group.groupId === column.group,
          );
          assert(isDefined(group));
          group.children.push(colDef);
          if (group.children.length === 1) {
            allDefinitions.push(group);
          }
        } else {
          allDefinitions.push(colDef);
        }
      });

      // Column group storing analysis parameter sources
      const sourcesGroup = selectedTable.columnGroups.find(
        (group) => group.definition?.kind === "sources",
      );
      const sourcesGroupDef = groupDefs.find(
        (def) => def.groupId === sourcesGroup?.id,
      );

      // If there are no sources mapped to analysis parameters, display a
      // placeholder column to cause the column group to render
      if (sourcesGroupDef && sourcesGroupDef.children.length === 0) {
        sourcesGroupDef.children.push(colDefSourcesPlaceholder);
        allDefinitions.push(sourcesGroupDef);
      }

      return allDefinitions;
    }, [
      selectedTable.columnGroups,
      selectedTable.columns,
      selectedTable.id,
      selectedTable.kind,
      suppressHeaderMenus,
    ]);

  const { columnOrder, columnDefs } = useMemo(() => {
    // Initial display order for columns
    const columnOrder = [
      // Row index column
      colDefRowIndex.colId,
      // Pinned columns
      ...selectedTable.columns
        .filter((column) => column.pinned)
        .map((column) => column.id),
      // Tool version column
      ...(selectedTable.kind === "analysis" ? [colDefToolVersion.colId] : []),
      // Placeholder analysis parameter source column
      ...(selectedTable.kind === "analysis"
        ? [colDefSourcesPlaceholder.colId]
        : []),
      // Unpinned parameter columns
      ...selectedTable.columns
        .filter((column) => !column.is_result && !column.pinned)
        .map((column) => column.id),
      // Compute resources column
      ...(selectedTable.kind === "analysis"
        ? [colDefComputeResources.colId]
        : []),
      // Unpinned result columns
      ...selectedTable.columns
        .filter((column) => column.is_result && !column.pinned)
        .map((column) => column.id),
      // Task columns
      ...(selectedTable.kind === "analysis"
        ? colGroupDefTask.children.map((child) => child.colId)
        : []),
      // Quick actions column
      ...(selectedTable.kind === "analysis" ? [colDefQuickActions.colId] : []),
    ];

    // All column definitions, order does not matter
    const columnDefs = [
      colDefRowIndex,
      ...customColDefs,
      ...(selectedTable.kind === "analysis"
        ? [
            colDefToolVersion,
            colDefComputeResources,
            colGroupDefTask,
            colDefQuickActions,
          ]
        : []),
    ];

    // Disable editing on all columns if user not permitted
    if (!isEditingPermitted) {
      disableEditing(columnDefs);
    }

    return { columnOrder, columnDefs };
  }, [
    customColDefs,
    isEditingPermitted,
    selectedTable.columns,
    selectedTable.kind,
  ]);

  // Notify grid when column order changes
  useEffect(() => {
    // Get the current column order
    const currColumnOrder = gridApi
      ?.getColumnState()
      ?.map(({ colId }) => colId);

    // Get the new column order
    const newColumnOrder = columnOrder.filter(
      (colId) => gridApi?.getColumn(colId) !== null,
    );

    // Notify grid to reorder columns when order changes
    if (!isEqual(currColumnOrder, newColumnOrder)) {
      gridApi?.applyColumnState({
        state: newColumnOrder.map((colId) => ({ colId })),
        applyOrder: true,
      });
    }
  }, [gridApi, columnOrder]);

  const rowData = useMemo(() => {
    return selectedTable.rows.map((row) => ({
      id: row.id,
      editable: row.editable,
      task: row.task,
      toolVersion: row.toolVersion,
      resourceTierId: row.resourceTierId,
      cells: row.cells,
    }));
  }, [selectedTable.rows]);

  const handleCellEditRequest = useCallback(
    (e: CellEditRequestEvent<DataTableRowData>) => {
      // Sanity check to ensure cells are not edited without permission
      if (!isEditingPermitted) {
        return;
      }

      const tableId = selectedTable.id;
      const columnId = e.column.getColId();
      const colIndex = selectedTable.columns.findIndex(
        ({ id }) => id === columnId,
      );

      // If column can't be found, do nothing
      if (colIndex === -1) {
        return;
      }

      const rowId = e.data.id;
      const oldFormula = e.data.cells[colIndex].formula;

      // We should never receive anything but a string, null or undefined. The
      // grid sends undefined if the cell edit has been canceled via
      // api.stopEditing. If we ever receive another type of value, there is an
      // AG Grid event that has not been handled.
      if (typeof e.newValue !== "string" && isNonNullish(e.newValue)) {
        captureException("Cell edit request received invalid formula");
        return;
      }

      // Cell edit request has been cancelled, do not update formula
      if (e.value === undefined) {
        return;
      }

      const newFormula = (e.newValue as string | null | undefined) ?? "";
      if (oldFormula !== newFormula) {
        void setCellFormula({
          address: { tableId, columnId, rowId },
          formula: newFormula,
        });
      }
    },
    [
      isEditingPermitted,
      selectedTable.columns,
      selectedTable.id,
      setCellFormula,
    ],
  );

  const handleSelectionChanged = useCallback(
    (e: SelectionChangedEvent<DataTableRowData>) => {
      const selectedRowIds = e.api.getSelectedRows().map((row) => row.id);
      setSelectedRowIds(selectedRowIds);
    },
    [setSelectedRowIds],
  );

  const handleColumnResized = useCallback(
    (e: ColumnResizedEvent<DataTableRowData>) => {
      // Wait until the user has finish dragging the resize handle before
      // persisting the new column width to the server
      const isColumnStillDragging = !e.finished;
      if (isColumnStillDragging || e.source !== "uiColumnResized") {
        return;
      }

      // Check that the resized column is custom column, as opposed to static
      // column like row index, compute resources, etc.
      const isColumnResizable = selectedTable.columns.some((column) => {
        return column.id === e.column?.getColId();
      });

      if (isNonNull(e.column) && isColumnResizable) {
        void resizeColumn({
          tableId: selectedTable.id,
          columnId: e.column.getColId(),
          newWidth: e.column.getActualWidth(),
          persistChange: isEditingPermitted,
        });
      }
    },
    [isEditingPermitted, resizeColumn, selectedTable.columns, selectedTable.id],
  );

  const handleColumnMoved = useCallback(
    (e: ColumnMovedEvent<DataTableRowData>) => {
      if (e.finished) {
        const oldColumnOrder = selectedTable.columns.map(({ id }) => id);
        const newColumnOrder = e.api
          .getColumnState()
          .map(({ colId }) => colId)
          // Remove any untracked columns (e.g. row index column)
          .filter((id) => oldColumnOrder.includes(id));

        if (!isEqual(newColumnOrder, oldColumnOrder)) {
          void setColumnOrder({
            tableId: selectedTable.id,
            columnIds: newColumnOrder,
          });
        }
      }
    },
    [selectedTable.columns, selectedTable.id, setColumnOrder],
  );

  const handleDragStarted = useCallback((e: DragStartedEvent) => {
    // Prevent cell editors from staying open when reordering columns
    e.api.stopEditing();
  }, []);

  const getRowId = useCallback(
    ({ data }: GetRowIdParams<DataTableRowData>) => data.id,
    [],
  );

  const handleGridReady = useCallback(
    (e: GridReadyEvent<DataTableRowData>) => setGridApi(e.api),
    [setGridApi],
  );

  const defaultColDef: AgGridReactProps["defaultColDef"] = useMemo(
    () => ({ suppressMovable: suppressReorderCols }),
    [suppressReorderCols],
  );

  return (
    <AgGridReact<DataTableRowData>
      key={selectedTable.id}
      gridId={selectedTable.id}
      className="ag-theme-balham-cell-borders"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      getRowId={getRowId}
      rowData={rowData}
      rowHeight={35}
      readOnlyEdit
      onGridReady={handleGridReady}
      onCellEditRequest={handleCellEditRequest}
      rowSelection="multiple"
      suppressRowClickSelection
      onDragStarted={handleDragStarted}
      suppressRowHoverHighlight
      suppressDragLeaveHidesColumns
      onSelectionChanged={handleSelectionChanged}
      onColumnResized={handleColumnResized}
      suppressMovableColumns={!isEditingPermitted}
      onColumnMoved={handleColumnMoved}
      maintainColumnOrder
      // Prevent the grid from automatically unpinning columns
      // https://www.ag-grid.com/react-data-grid/grid-options/#reference-columnPinning-processUnpinnedColumns
      processUnpinnedColumns={() => []}
      reactiveCustomComponents
      // Set the initial column order. This allows column group children to be
      // rendered separately when the grid first renders.
      initialState={{
        columnPinning: {
          leftColIds: [
            colDefRowIndex.colId,
            ...selectedTable.columns
              .filter((column) => column.pinned)
              .map((column) => column.id),
          ],
          rightColIds: [
            ...(selectedTable.kind === "analysis"
              ? [colDefQuickActions.colId]
              : []),
          ],
        },
        columnOrder: {
          orderedColIds: columnOrder,
        },
      }}
      // HyperFormula does not support copying multiple cell ranges
      suppressMultiRangeSelection
      suppressContextMenu
      enableRangeSelection
      enableFillHandle={isEditingPermitted}
      sendToClipboard={sendToClipboard}
      processDataFromClipboard={processDataFromClipboard}
      fillOperation={fillOperation}
    />
  );
};

interface DataTableProps {
  suppressHeaderMenus?: boolean;
  suppressReorderCols?: boolean;
}

export const DataTable = memo(function DataTable(props: DataTableProps) {
  const selectedTableId = useDataTableContext((s) => s.selectedTableId);
  const isRefetching = useDataTableContext((s) => s.isRefetching);

  if (isRefetching) {
    return <AppLoading />;
  }

  return isDefined(selectedTableId) ? (
    <DataTableInner {...props} />
  ) : (
    <AgGridReact
      key="empty"
      className="ag-theme-balham-cell-borders"
      columnDefs={[]}
      rowData={[]}
    />
  );
});
